"use client";

import dynamic from "next/dynamic";
import { useState, type FocusEvent, type PointerEvent } from "react";

import {
  RASHI_CHECK_FIELD_IDS,
  RASHI_CHECK_FORM,
  RASHI_CHECK_FRAME,
} from "@/components/rashi/rashi-check-form";
import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";

import { HEALTH_RASHI_CHECK_RESERVE } from "./health-rashi-check-form";

// The /health-horoscope/ check's boundary, the /yearly-rashifal/<sign>/
// check's pattern (YearlyRashiCheckLazy): the server page imports only this
// module, and it owns the one dynamic() to the island. The form, place search,
// kundali client, verdict and analytics load as an async chunk; the first load
// gains only this file, the shell and the rashi check's form props.
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader scrolling the guide never downloads the island and the deploy's
// body pointerdown smoke loads nothing; a press, focus or submit on the shell
// does.
//
// The slot reserves the tallest state and every state fills it, so neither
// shell -> form nor a slow engine reply -> answer (or error) moves the sign
// grid below. Natural heights measured 2026-10-18 (next dev +
// Playwright, POST /kundali mocked, 8 widths from 320 to 1280): the shell is
// 436 at 320-390 px wide and 420 above; the form 436, 420 from 414 and 362
// from 640. The tallest state is the engine's place error after a typed place
// nobody picked: 560 at 320-390, 528 at 414, 510 at 480-639 and 452 from 640;
// an engine failure (500, after its retries) is 522 at 320 and 430 from 640.
// The tallest answer (Vrishchik (Scorpio) with a boundary note naming Dhanu
// (Sagittarius)) is 420 at 320-360 and 351 from 640; the day's windows 371 at
// 320, a whole day 322 and needs-a-time 268. No answer outgrows the form's
// error state, so one reserve holds: the rashi check's 600 px on phones and
// 500 px from the sm breakpoint. With it the slot held 600/500 in every state
// (48 runs), the content below never moved, the submit stayed put from press
// to reply, and "Check My Rashi" stays on one line from 320.
// translate="no" covers the shell, form and answer, which re-render on "check
// another" (tests/translate-safe-islands.test.ts).
const SHELL_PROPS = { ...RASHI_CHECK_FORM, className: RASHI_CHECK_FRAME } as const;

const HealthRashiCheck = dynamic(() => import("./HealthRashiCheck"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL_PROPS} />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: RASHI_CHECK_FIELD_IDS.date,
  tob: RASHI_CHECK_FIELD_IDS.time,
  place: RASHI_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || RASHI_CHECK_FIELD_IDS.date;
}

export default function HealthRashiCheckLazy() {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // The shell's own onActivate cannot tell a keyboard focus from a touch, so
  // the slot reads the intent in the capture phase: focus follows the visitor
  // into the real form after a keyboard focus, a mouse press or a submit, and
  // never after a touch, which may be the start of a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(RASHI_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-health-rashi-check-slot=""
      className={`flex flex-col ${HEALTH_RASHI_CHECK_RESERVE}`}
      {...shellIntent}
    >
      {active ? (
        <HealthRashiCheck focusFieldId={focusFieldId} />
      ) : (
        <BirthToolFormShell {...SHELL_PROPS} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
